import { Inject, Injectable, LOCALE_ID } from '@angular/core';
import { AngularFireDatabase, AngularFireObject } from '@angular/fire/compat/database';
import { filter, firstValueFrom, of, Subscription, timeout } from 'rxjs';
import { NGXLogger as LoggerService } from "ngx-logger";
import firebase from 'firebase/compat/app';
import { DbUser, Delegation as DelegationRecord } from 'functions-lib';
import { EssErrorService } from 'ngx-essentia';
import { adoptLangPref, normalizeLanguage } from '../shared/services/lang-pref';
import { isPermissionDenied } from '../shared/services/db-errors';
import { ConnectionStatusService } from '../shared/services/connection-status.service';
import { RETRY_PAUSE_AFTER_MAXRETRY_MS, RETRY_WAIT_FOR_CONNECTION_MS, describeError, transientTransactionAbort } from '../shared/services/rtdb-transaction-errors';
const DB_PATH_TO_USERS = 'users/';
/** message suffix of the signed-in-uid guard (persistDbUser / createDbUser) - callers downgrade such errors to a warning */
const NOT_SIGNED_IN_USER_SUFFIX = ' is not the signed-in user';
/** how long whenOwnDbUserLoaded waits for the own record on the listen before it falls back to the create */
const WHEN_LOADED_TIMEOUT_MS = 15000;
/** every record needs these - a record without one of them is incomplete and gets repaired (createDbUser) */
const REQUIRED_USER_FIELDS: Array<'uid' | 'secretKey' | 'language'> = ['uid', 'secretKey', 'language'];

function missingUserFields(dbUser: DbUser): string[] {
    return REQUIRED_USER_FIELDS.filter(field => !dbUser?.[field]);
}

function isNotSignedInUserError(error: unknown): boolean {
    return typeof (error as Error)?.message === 'string'
        && (error as Error).message.endsWith(NOT_SIGNED_IN_USER_SUFFIX);
}

/**
 * What ensureDbUser did, and if `dbUser` is still null afterwards, why. The reasons mirror the log
 * levels: `not-signed-in` and `permission-denied` are warnings (login/logout races), `failed` is an
 * error. `transient` (connection drop, concurrent write) is retried once; reported here it means the
 * retry was aborted too - a warning with its own prefix that heals itself (see createAndLoadDbUser).
 * `exists-but-unreadable` is the signature of two create transactions overtaking each other - must
 * not occur since ensureDbUser shares one per uid.
 */
export type EnsureDbUserFailure =
    { loaded: false; reason: 'not-signed-in' | 'transient' | 'permission-denied' | 'exists-but-unreadable' | 'failed'; detail: string };
export type EnsureDbUserOutcome =
    | { loaded: true; action: 'create' | 'repair' | 'exists' }
    | EnsureDbUserFailure;

/**
 * Provides additional user information that is NOT stored in afAuthUser in FirebaseAuthService
 * We do not duplicate the information from afAuthUser so we don't get any sync challenges
 * dbUser is loaded and updated via subscribeDbUser which is triggered from AuthService
 */
@Injectable({
    providedIn: 'root'
})
export class UserService {
    public dbUser: DbUser;
    dbUserSubscription: Subscription;
    /**
     * Registered by the AuthService (which this service must not inject - circular DI) and called with
     * every record emitted by subscribeDbUser, so that the AuthService can react to `claimsUpdatedAt`.
     */
    private claimsListener: (dbUser: DbUser) => void = null;
    /** the create/load that is currently running per uid - every caller awaits the same one (see ensureDbUser) */
    private pendingEnsure = new Map<string, Promise<EnsureDbUserOutcome>>();
    constructor(
        private afd: AngularFireDatabase,
        private logger: LoggerService,
        private errorService: EssErrorService,
        @Inject(LOCALE_ID) private locale: string,
        private connectionStatus: ConnectionStatusService,
    ) {
    }

    /**
     * (re-)subscribe to the dbUser in case the uid of afAuthUser has changed
     * Should be called ONLY from AuthService when the authUser changed
     * @param uid
     */
    public subscribeDbUser(uid: string) {
        this.unsubscribeDbUser();
        // drop the record of the previous auth user - otherwise it would survive a logout → anonymous switch
        this.dbUser = null;
        if (!uid) {
            return;
        }
        // Only the FIRST `null` of a subscription means "record does not exist yet" (e.g. login with
        // redirect where the popup flow could not create it). A `null` AFTER a loaded record means the
        // record was removed server-side (account deletion) and must never be recreated.
        // Retry note (checked 2026-09-16): if the create transaction is aborted (e.g. 'disconnect'), this
        // subscription does NOT emit a second `null` - the value did not change (applyLocally=false) and
        // RTDB raises value events only on changes, also after a reconnect. ensureDbUser retries an
        // aborted create once; if that fails too, the next writer of the record (persistDbUser,
        // updateUserToTriggerSyncWithBigQuery) or reader of the key (whenOwnDbUserLoaded) ensures it
        // again, at the latest the next subscription of this uid (hourly token refresh, login, page load).
        let seenRecord = false;
        // one repair per subscription - a record that stays incomplete must not loop
        let repairRequested = false;
        this.dbUserSubscription = this.getDbUserObservable(uid).subscribe({
            next: dbUser => {
                if (dbUser) {
                    seenRecord = true;
                    this.logger.log('subscribeDbUser: ', dbUser);
                    // set also when incomplete (the claims listener needs the emission) - readers of the
                    // key wait for the complete record (whenOwnDbUserLoaded)
                    this.dbUser = dbUser;
                    const missingFields = missingUserFields(dbUser);
                    if (missingFields.length && !repairRequested && this.isCurrentAuthUid(uid)) {
                        // a write that reached the server without the record (an update after an aborted
                        // create) or a legacy record - complete it now, not on the next login
                        repairRequested = true;
                        this.logger.warn('subscribeDbUser: incomplete record, repairing', uid, missingFields);
                        void this.ensureDbUser(uid);
                    }
                    // Wer sich auf einem frischen Geraet einloggt, bringt seine
                    // Sprache aus dem Account mit — aber nur, solange lokal noch
                    // keine explizite Wahl getroffen wurde. Kein Redirect.
                    adoptLangPref(dbUser.language);
                    this.claimsListener?.(dbUser);
                    return;
                }
                if (seenRecord) {
                    this.logger.warn('subscribeDbUser: dbUser removed, not recreating', uid);
                    this.dbUser = null;
                    return;
                }
                if (!this.isCurrentAuthUid(uid)) {
                    // stale subscription of a previous auth user (login/logout race) - the rules would
                    // reject the write anyway (`.write: $uid === auth.uid`)
                    this.logger.warn('subscribeDbUser: uid is no longer the auth user, skipping create', uid);
                    return;
                }
                this.logger.log('subscribeDbUser: no dbUser found, creating...', uid);
                void this.ensureDbUser(uid);
            },
            error: error => {
                // The server revokes the listen when the auth token changes (account merge/login) or
                // disappears (account deletion) - nothing to do for a record we may no longer read,
                // and nothing worth a /uilogging report.
                if (!this.isCurrentAuthUid(uid) || isPermissionDenied(error)) {
                    this.logger.warn('subscribeDbUser: listen revoked for uid', uid, error);
                    return;
                }
                this.logger.error('subscribeDbUser: subscription failed for uid ' + uid + ' - ' + error);
            },
        });
    }

    /** @see claimsListener */
    public setClaimsListener(listener: (dbUser: DbUser) => void): void {
        this.claimsListener = listener;
    }

    /**
     * Closes the current dbUser subscription (the record itself is kept). Called synchronously by the
     * AuthService as soon as the auth user changes, before the server revokes the listen.
     */
    public unsubscribeDbUser(): void {
        this.dbUserSubscription?.unsubscribe();
    }

    /**
     * The UserService must not depend on the AuthService (circular DI), so the signed-in uid is
     * read from the compat auth instance directly.
     */
    private isCurrentAuthUid(uid: string): boolean {
        return firebase.auth().currentUser?.uid === uid;
    }

    /**
     * Fail fast instead of a PERMISSION_DENIED from the rules (`.write: $uid === auth.uid`);
     * logError=false because the callers handle and log the error themselves
     * (`createAndLoadDbUser` downgrades it to a warning, see isNotSignedInUserError).
     */
    private assertCurrentAuthUid(uid: string, caller: string): void {
        if (!this.isCurrentAuthUid(uid)) {
            throw this.errorService.newError(`${caller}: uid ${uid}${NOT_SIGNED_IN_USER_SUFFIX}`, false);
        }
    }

    getDbUserObservable(uid: string) {
        if (uid) {
            return this.getUserRef(uid).valueChanges();
        }
        return null;
    }

    /**
     * Ensures that `users/{uid}` exists and is loaded into `dbUser`. Concurrent callers for the same
     * uid share ONE create transaction: a second create-if-missing transaction started while the first
     * is still unacknowledged sees the first one's hidden pending write (applyLocally=false), aborts
     * locally as "exists" without any server contact, and the listener read after it still returns
     * null - the access link then failed with "no user record" although the record was on its way
     * (see `_whocan-info/product/workorder-setnewdelegationrecord-no-user-record-2026-09-16.md`).
     * Callers: subscribeDbUser (first `null`, incomplete record), loadUser (every login), every write
     * to the record (ensureRecordBeforeUpdate) and whenOwnDbUserLoaded.
     * An aborted create is retried once inside the shared promise, so every waiting caller gets the
     * result of the retry. Never rejects - the outcome says what happened.
     */
    public ensureDbUser(uid: string): Promise<EnsureDbUserOutcome> {
        const running = this.pendingEnsure.get(uid);
        if (running) {
            this.logger.log('ensureDbUser: create/load already running, awaiting it', uid);
            return running;
        }
        const promise = this.createAndLoadDbUser(uid).finally(() => {
            if (this.pendingEnsure.get(uid) === promise) {
                this.pendingEnsure.delete(uid);
            }
        });
        this.pendingEnsure.set(uid, promise);
        return promise;
    }

    /**
     * Legt den User-Record an (oder vervollstaendigt ihn), falls noetig, und laedt ihn danach in `dbUser`.
     * Ueberschreibt NIE `secretKey` oder `language` eines bestehenden Records - der Key ist die
     * Basis aller verschickten Zugangslinks und Delegationen (siehe
     * `_whocan-info/product/workorder-secretkey-rotation-on-login-2026-09-14.md`).
     * Wird bei jedem Login aufgerufen (loadUser), darf also beliebig oft laufen - aber nur ueber
     * ensureDbUser, damit pro uid nie zwei Transactions gleichzeitig laufen.
     * Eine abgebrochene Transaction (`transient`) wird genau einmal wiederholt; scheitert auch der
     * Retry, ist das ein Warn mit eigenem stabilen Praefix (ohne uid, damit Error Reporting gruppiert) -
     * siehe `_whocan-info/product/workorder-dbuser-create-aborted-by-update-2026-10-04.md`, Tasks 4 + 5;
     * Einstufung warn seit `workorder-ensure-key-disconnect-retry-2026-10-30.md`, Task 5.
     */
    private async createAndLoadDbUser(uid: string): Promise<EnsureDbUserOutcome> {
        const first = await this.tryCreateAndLoadDbUser(uid);
        if (first.outcome.loaded !== false || first.outcome.reason !== 'transient') {
            return first.outcome;
        }
        this.logger.warn('ensureDbUser: create aborted, retrying once', uid, first.outcome.detail);
        if (first.abortReason === 'disconnect') {
            await this.connectionStatus.waitForConnected(RETRY_WAIT_FOR_CONNECTION_MS);
        } else if (first.abortReason === 'maxretry') {
            await new Promise(resolve => setTimeout(resolve, RETRY_PAUSE_AFTER_MAXRETRY_MS));
        }
        // 'set' / 'overriddenBySet': the write that got in the way is through - retry right away
        const { outcome } = await this.tryCreateAndLoadDbUser(uid);
        if (outcome.loaded === false && outcome.reason === 'transient') {
            // warn, not error (workorder-ensure-key-disconnect-retry-2026-10-30, task 5): the retry died on a
            // flapping connection too, which heals itself - the next writer or reader of the record ensures it
            // again, at the latest the next subscription of this uid (see subscribeDbUser)
            this.logger.warn(`ensureDbUser: create failed after retry (${outcome.reason}: ${outcome.detail})`, uid);
        }
        return outcome;
    }

    /**
     * One attempt of createAndLoadDbUser. Faengt jeden Fehler ab (die Subscription ruft fire-and-forget)
     * und meldet ihn als Outcome; die Einstufung warn/error ist die aus
     * `workorder-dbuser-transient-errors-warn-2026-09-16.md`. `abortReason` nennt bei `transient` den
     * Abbruchgrund der Transaction.
     */
    private async tryCreateAndLoadDbUser(uid: string): Promise<{ outcome: EnsureDbUserOutcome; abortReason?: string }> {
        this.logger.log('createAndLoadDbUser: ensuring record...', uid);
        try {
            if (!uid) {
                throw this.errorService.newError('uid is null')
            }
            const { action, record } = await this.createDbUser(uid);
            if (record) {
                // the server acknowledged exactly this value - take it from the transaction result instead
                // of reading it back through a listener, whose cache may still be null while the ack
                // and the server push race
                this.dbUser = record;
                return { outcome: { loaded: true, action } };
            }
            // record complete, nothing written: load it. We need the loaded user for example to set a
            // delegation right after merging the account, otherwise the check for an existing
            // delegation would fail
            await this.loadDbUser(uid);
            if (!this.dbUser) {
                // the transaction saw a record (a hidden pending write of another transaction), the
                // listener does not - exactly the race ensureDbUser prevents; if it shows up, we want to know
                const detail = 'record pending but not readable yet';
                this.logger.warn(`createAndLoadDbUser: ${detail}`, uid);
                return { outcome: { loaded: false, reason: 'exists-but-unreadable', detail } };
            }
            return { outcome: { loaded: true, action } };
        } catch (error) {
            const detail = describeError(error);
            const message = 'Failed to create/load user (uid: ' + uid + ' - ' + detail + ')';
            if (isNotSignedInUserError(error)) {
                // expected during login/logout races - nothing to report
                this.logger.warn(message);
                return { outcome: { loaded: false, reason: 'not-signed-in', detail } };
            }
            const abortReason = transientTransactionAbort(error);
            if (abortReason) {
                // the transaction was aborted by the connection or a concurrent write, not by a rule or
                // a bug - the caller retries once
                this.logger.warn(message);
                return { outcome: { loaded: false, reason: 'transient', detail }, abortReason };
            }
            if (isPermissionDenied(error) && !this.isCurrentAuthUid(uid)) {
                // the signed-in user changed while the transaction (or the load after it) was reading -
                // same race as above, just surfaced by the rules instead of the guard
                this.logger.warn(message);
                return { outcome: { loaded: false, reason: 'permission-denied', detail } };
            }
            // still the signed-in user and still denied -> a real rules or token problem, keep it visible
            this.logger.error(message);
            return { outcome: { loaded: false, reason: 'failed', detail } };
        }
    }

    /**
     * Atomic create-if-missing of `users/{uid}` via RTDB transaction:
     *  - no record: create `{uid, language, secretKey}` once,
     *  - incomplete record (an update that reached the server without the record, e.g. `{updateTrigger}`
     *    after an aborted create, or a legacy record): add `uid`, `secretKey`, `language` where they
     *    are MISSING - an existing value is never overwritten,
     *  - complete record: abort without writing - never rotate the key or reset the language.
     * Before 2026-09-14 this was an `update()` that rotated the secretKey on EVERY login and thereby
     * silently invalidated every access link / delegation issued with the previous key.
     * @returns the action and, when something was written, the record the server acknowledged
     */
    private async createDbUser(uid: string): Promise<{ action: 'create' | 'repair' | 'exists'; record: DbUser | null }> {
        this.logger.log('createDbUser: ensure record exists', uid);
        this.assertCurrentAuthUid(uid, 'createDbUser');
        const ref = this.afd.database.ref(`${DB_PATH_TO_USERS}${uid}`);
        // The update function may run several times (first optimistically on the local cache, which
        // is usually empty right after login, then again on the server value) - so no logging inside,
        // and applyLocally=false so the optimistic "create" never reaches subscribeDbUser.
        let action: 'create' | 'repair' | 'exists' = 'exists';
        const result = await ref.transaction((current: DbUser | null) => {
            if (current === null) {
                // first contact with this account: create the full record once
                action = 'create';
                const created: DbUser = { uid, language: this.locale, secretKey: this.generateSecretKey() };
                return created;
            }
            if (missingUserFields(current).length) {
                action = 'repair';
                return {
                    ...current,
                    uid: current.uid || uid,
                    language: current.language || this.locale,
                    secretKey: current.secretKey || this.generateSecretKey(),
                };
            }
            action = 'exists';
            return undefined; // abort: record complete, nothing to write
        }, undefined, false);
        // committed=false covers both a local abort (record in the cache) and the SDK's 'nodata' (record
        // appeared on the server during a rerun) - the SDK completes 'nodata' without an error
        if (result.committed) {
            this.logger.log(`createDbUser: written (${action})`, uid);
            return { action, record: result.snapshot?.val() ?? null };
        }
        this.logger.log('createDbUser: record exists, nothing written', uid);
        return { action: 'exists', record: null };
    }

    private generateSecretKey(): string {
        // This is not 100% unguessable but should be good enough for our purpose
        // For details check: https://firebase.blog/posts/2015/02/the-2120-ways-to-ensure-unique_68
        let d = new Date().getTime(); // Timestamp
        let d2 = ((typeof performance !== "undefined") &&
            performance.now && (performance.now() * 1000)) || 0; // Time in microseconds since page-load or 0 if unsupported
        return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
            let r = Math.random() * 16; // random number between 0 and 16
            if (d > 0) { // Use timestamp until depleted
                r = (d + r) % 16 | 0;
                d = Math.floor(d / 16);
            } else { // Use microseconds since page-load if supported
                r = (d2 + r) % 16 | 0;
                d2 = Math.floor(d2 / 16);
            }
            return (c === "x" ? r : (r & 0x3 | 0x8)).toString(16);
        });
    }

    /**
     * `update()` on a node that does not exist creates it - with nothing but the updated fields (the
     * rules have no `.validate`). And an update while the create transaction is still running aborts
     * that transaction ('set'). So every update of `users/{uid}` first awaits the record: the running
     * create if there is one, otherwise a create/repair unless the complete record is already loaded.
     * @returns null when the record is there, otherwise the outcome that says why it is not
     */
    private async ensureRecordBeforeUpdate(uid: string): Promise<EnsureDbUserFailure | null> {
        const loaded = this.dbUser?.uid === uid && !!this.dbUser?.secretKey;
        if (loaded && !this.pendingEnsure.has(uid)) {
            return null;
        }
        const outcome = await this.ensureDbUser(uid);
        return outcome.loaded === false ? outcome : null;
    }

    private async persistDbUser(userDataUpdate: DbUser, uid: string): Promise<void> {
        if (!uid) {
            return;
        }
        this.assertCurrentAuthUid(uid, 'persistDbUser');
        const missing = await this.ensureRecordBeforeUpdate(uid);
        if (missing) {
            // the callers (language choice, account deletion, delegation) must learn that nothing was written
            this.logger.warn('persistDbUser: no user record, not writing', uid, missing.reason, missing.detail);
            throw this.errorService.newError(
                `persistDbUser: no user record for uid ${uid} (${missing.reason}: ${missing.detail})`, false);
        }
        return await this.getUserRef(uid).update(userDataUpdate);
    }

    /**
     * Haelt die Mail-Sprache am Account aktuell. `DbUser.language` wurde frueher
     * nur bei der Anlage geschrieben und fror damit auf den (ggf. anonymen)
     * Erstellungszeitpunkt ein — Deadline- und Reminder-Mails kamen dauerhaft in
     * der falschen Sprache. Wird bei jeder expliziten Sprachwahl aufgerufen.
     */
    public async updateLanguage(uid: string, language: string): Promise<void> {
        const normalized = normalizeLanguage(language);
        if (!uid || !normalized || this.dbUser?.language === normalized) {
            return;
        }
        this.logger.log('updateLanguage', uid, normalized);
        await this.persistDbUser({ language: normalized }, uid);
    }

    public async updateUserToTriggerSyncWithBigQuery(uid: string): Promise<void> {
        if (!uid) {
            return;
        }
        const missing = await this.ensureRecordBeforeUpdate(uid);
        if (missing) {
            // the trigger is dispensable: creating the record triggers the export anyway
            this.logger.warn('updateUserToTriggerSyncWithBigQuery: no user record, not writing', uid, missing.reason, missing.detail);
            return;
        }
        const uniqueId = this.afd.createPushId();
        await this.getUserRef(uid).update({ updateTrigger: uniqueId });
    }

    public async loadDbUser(uid: string): Promise<void> {
        if (uid) {
            this.logger.log('loadDbUser', uid);
            this.dbUser = await firstValueFrom(this.getDbUserObservable(uid));
            this.logger.log('loadDbUser loaded', this.dbUser);
        }
        return;
    }

    private getUserRef(uid: string): AngularFireObject<DbUser> {
        return this.afd.object(`${DB_PATH_TO_USERS}${uid}`);
    }

    public async getDelegationRecord(uid: string): Promise<DelegationRecord> {
        if (!this.dbUser) {
            await this.loadDbUser(uid);
        }
        const secretKey = this.dbUser.secretKey;
        const newDelegation: DelegationRecord = {
            delegatedFromUid: this.dbUser.uid,
            secretKey: secretKey
        };
        return newDelegation;
    }

    get secretKey(): string {
        return this.dbUser?.secretKey;
    }

    /**
     * Sets a new delegation record on the specified user
     * A new delegationRecord triggers a backend function 'setDelegationCustomClaim' that sets the custom claim on the authUser
     * @param newDelegationRecord The delegation record to add
     * @param uid The user ID to set the delegation on
     * @returns true when the record was actually persisted. false means the delegation was already
     * there, so the trigger does NOT fire again and no claims signal is on its way - the caller must
     * then decide from the state it already has instead of waiting for one.
     */
    public async setNewDelegationRecord(newDelegationRecord: DelegationRecord, uid: string): Promise<boolean> {
        // Validate inputs
        if (!uid) {
            this.logger.error('setNewDelegationRecord: uid is required');
            throw this.errorService.newError('uid is required');
        }
        if (!newDelegationRecord?.delegatedFromUid || !newDelegationRecord?.secretKey) {
            this.logger.error('setNewDelegationRecord: invalid delegation record', newDelegationRecord);
            throw this.errorService.newError('Invalid delegation record');
        }
        // Load user data
        await this.loadDbUser(uid);
        if (!this.dbUser) {
            // no record yet (fresh provider/e-mail account after the merge flow unsubscribed, or an
            // auth session whose record was never created) - ensure it here; if the subscription's
            // create is still on its way, this awaits that same transaction instead of starting a second
            this.logger.log('setNewDelegationRecord: no dbUser yet, ensuring', uid);
            const outcome = await this.ensureDbUser(uid);
            if (outcome.loaded === false) {
                // the prefix is the Cloud Logging query, the parenthesis carries the cause
                throw this.errorService.newError(
                    `setNewDelegationRecord: no user record for uid ${uid} (${outcome.reason}: ${outcome.detail})`);
            }
        }
        if (!this.dbUser) {
            throw this.errorService.newError(
                `setNewDelegationRecord: no user record for uid ${uid} (ensure reported loaded, dbUser still null)`);
        }
        this.logger.log('setNewDelegationRecord', newDelegationRecord, this.dbUser.delegationFrom);
        // Get existing delegation records or initialize empty array
        const existingRecords = this.dbUser.delegationFrom ?? [];
        // Check if delegation already exists
        if (this.delegationRecordExistsAlready(existingRecords, newDelegationRecord)) {
            this.logger.log('Delegation record already exists, skipping...');
            return false;
        }
        // Add new delegation record
        this.logger.log('Adding new delegation record...');
        const updatedRecords = [...existingRecords, newDelegationRecord];
        // Persist to database
        await this.persistDbUser({ delegationFrom: updatedRecords }, uid);
        // Reload user to get fresh data
        await this.loadDbUser(uid);
        this.logger.log('setNewDelegationRecord completed', this.dbUser.delegationFrom);
        return true;
    }

    private delegationRecordExistsAlready(delegationRecords: DelegationRecord[], delegationRecord: DelegationRecord): boolean {
        if (!delegationRecords || delegationRecords.length === 0) {
            return false;
        }
        const findIndex = delegationRecords.findIndex(value =>
            value.delegatedFromUid === delegationRecord.delegatedFromUid &&
            value.secretKey === delegationRecord.secretKey
        );
        this.logger.log('delegationRecordExistsAlready check', findIndex, delegationRecord, delegationRecords);
        return findIndex !== -1;
    }

    /**
     * Resolves with the own user record once it is loaded. Right after a sign-in `dbUser` is still null
     * while subscribeDbUser creates/loads the record asynchronously - every synchronous reader of
     * `secretKey` / `delegationFrom` (access link, get-poll-access, iCal) awaits this first. Shares the
     * running create with the subscription (ensureDbUser); rejects with the reason when the record
     * cannot be loaded (`workorder-auth-window-option-access-link-2026-10-04.md`, tasks 1-3).
     */
    public async whenOwnDbUserLoaded(uid: string): Promise<DbUser> {
        if (this.dbUser && this.dbUser.uid === uid) {
            return this.dbUser;
        }
        if (!uid) {
            throw this.errorService.newError('whenOwnDbUserLoaded: no signed-in uid');
        }
        // Wait for the record on the listen first: for a signed-in user it normally exists and only has not
        // arrived yet. A value without uid/secretKey is the optimistic local view of an update in flight
        // (e.g. the updateTrigger of a vote with a display name) before the server value - skipped. The
        // create transaction is reserved for a record the server reports as missing (null); ensureDbUser
        // retries an aborted one once.
        const record = await firstValueFrom(this.getDbUserObservable(uid).pipe(
            filter(value => value === null || value?.uid === uid || !!value?.secretKey),
            timeout({ first: WHEN_LOADED_TIMEOUT_MS, with: () => of(undefined as DbUser | undefined) })
        ));
        if (record) {
            this.dbUser = record;
            return record;
        }
        const outcome = await this.ensureDbUser(uid);
        if (outcome.loaded === false) {
            throw this.errorService.newError(
                `whenOwnDbUserLoaded: no user record for uid ${uid} (${outcome.reason}: ${outcome.detail})`);
        }
        if (!this.dbUser) {
            throw this.errorService.newError(
                `whenOwnDbUserLoaded: no user record for uid ${uid} (ensure reported loaded, dbUser still null)`);
        }
        return this.dbUser;
    }

    public getSecretKey(uid: string): string {
        if (!this.dbUser) {
            // own prefix BEFORE the delegation branch: a record that is not loaded yet (fresh sign-in)
            // must stay distinguishable in the log from a delegation that is really missing
            throw this.errorService.newError(`getSecretKey: dbUser not loaded (uid: ${uid})`);
        }
        if (!uid || uid === this.dbUser.uid) {
            if (!this.dbUser.secretKey) {
                throw this.errorService.newError("No secret key for own user id: " + uid);
            }
            return this.dbUser.secretKey;
        }

        const findResult = this.dbUser.delegationFrom?.find(value => value.delegatedFromUid === uid);
        if (findResult) {
            return findResult.secretKey;
        }
        throw this.errorService.newError("Could not find secret key for uid: " + uid);
    }

    public logout(): void {
        this.dbUser = null;
        this.unsubscribeDbUser();
    }

    /**
     * Sets a time stamp so that the firebase functions job will delete the user after one or two days
     */
    public async markForDeletion(uid: string): Promise<void> {
        this.logger.log('markForDeletion');
        const userDataUpdate: DbUser = {
            markedForDeletion: firebase.database.ServerValue.TIMESTAMP
        };
        await this.persistDbUser(userDataUpdate, uid);
    }

    public async unMarkForDeletion(uid: string): Promise<void> {
        this.logger.log('unMarkForDeletion ');
        const userDataUpdate: DbUser = {
            markedForDeletion: null,
        };
        await this.persistDbUser(userDataUpdate, uid);
    }
}
